import {
  ApplySchemaAttributes,
  command,
  CommandFunction,
  extension,
  ExtensionTag,
  getTextSelection,
  keyBinding,
  KeyBindingProps,
  MarkExtension,
  MarkExtensionSpec,
  MarkSpecOverride,
  NamedShortcut,
  PrimitiveSelection,
  ProsemirrorAttributes,
  Static,
  toggleMark,
} from '@remirror/core';
import { CoreIcon } from '@remirror/icons';
import React from 'react';
import { InputRule, markInputRule } from 'remirror';

import { WFormatTypeProperty } from './_interface';

const CREATE_COMMAND_LABEL = 'Toggle Bold';

const toggleWFormatBoldOptions: Remirror.CommandDecoratorOptions = {
  icon: 'bold',
  label: ({ t }) => t(CREATE_COMMAND_LABEL),
  shortcut: NamedShortcut.Bold,
};

export type WFormatExtensionAttributes = ProsemirrorAttributes<{
  /**
   * The Mark type.
   */
  type?: WFormatTypeProperty;
}>;
export interface WFormatBoldOptions {
  /**
   * Require set the mark type property for this extension.
   */
  type?: Static<WFormatTypeProperty>;
  defaultType?: Static<WFormatTypeProperty>;
}

/**
 * When added to your editor it will provide the `toggleBold` command which makes the
 * text under the cursor / or at the provided position range bold.
 */
@extension<WFormatBoldOptions>({
  defaultOptions: {
    type: undefined,
    defaultType: 'bold',
  },
  staticKeys: ['defaultType', 'type'],
})
export class WFormatBoldExtension extends MarkExtension<WFormatBoldOptions> {
  get name() {
    return 'wFormatBold' as const;
  }

  createTags() {
    return [ExtensionTag.FormattingMark, ExtensionTag.FontStyle];
  }

  createMarkSpec(extra: ApplySchemaAttributes, override: MarkSpecOverride): MarkExtensionSpec {
    return {
      ...override,
      attrs: {
        ...extra.defaults(),
        type: { default: this.options.defaultType },
      },
      parseDOM: [
        {
          tag: `w-format[type=bold]`,
          getAttrs: (element: string | Node) => ({
            ...extra.parse(element),
            type: 'bold',
          }),
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: (mark) => {
        const { type } = this.options;
        return ['w-format', { type: mark.attrs.type.toString() }, 0];
      },
    };
  }

  createInputRules(): InputRule[] {
    return [
      markInputRule({
        regexp: /(?:\*\*|__)([^*_]+)(?:\*\*|__)$/,
        type: this.type,
        ignoreWhitespace: true,
      }),
    ];
  }

  /**
   * Toggle the bold styling on and off. Remove the formatting if any
   * matching bold formatting within the selection or provided range.
   */

  @command(toggleWFormatBoldOptions)
  toggleWFormatBold(attrs?: WFormatExtensionAttributes, selection?: PrimitiveSelection): CommandFunction {
    return toggleMark({ type: this.type, selection, attrs: { ...attrs, type: 'bold' } });
  }

  /**
   * Set the bold formatting for the provided range.
   */
  @command()
  setWFormatBold(selection?: PrimitiveSelection): CommandFunction {
    return ({ tr, dispatch }) => {
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);
      dispatch?.(tr.addMark(from, to, this.type.create()));

      return true;
    };
  }

  /**
   * Remove the bold formatting from the provided range.
   */
  @command()
  removeWFormatBold(selection?: PrimitiveSelection): CommandFunction {
    return ({ tr, dispatch }) => {
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);

      if (!tr.doc.rangeHasMark(from, to, this.type)) {
        return false;
      }

      dispatch?.(tr.removeMark(from, to, this.type));

      return true;
    };
  }

  @keyBinding({ shortcut: NamedShortcut.Bold, command: 'toggleWFormatBold' })
  shortcut(props: KeyBindingProps): boolean {
    return this.toggleWFormatBold()(props);
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Remirror {
    interface AllExtensions {
      wFormatBold: WFormatBoldExtension;
    }
  }
}
